import { useGetSettingsPermissionsEmployees } from '@agos/api-client/auth'
import { effectiveRoleKey, formatCount, T, tt } from '@agos/shared'
import { Spinner } from '@agos/ui'
import { Server } from 'lucide-react'
import { useMemo, useState } from 'react'

import { allowedOf, useRolesOverview } from '../../roles/roles-api'
import { holderCount, holdersOf, type HolderGroup, type PersonRights } from '../holders'
import { displayParentOf, groupCatalogue, permissionDescription, permissionLabel } from '../permission-matrix'
import { roleNameOf } from '../role-model'
import { RoleChip } from '../RoleChip'
import { TabReadFailed } from '../TabReadFailed'

/**
 * *Kto čo smie* - pick a right, see who holds it and why. The main administrator's tab (design §2.9).
 *
 * Two reads, both needed for an honest answer: the overview (roles and their people) and the exceptions with the
 * server's `allowed`. Either failing replaces the answer with one block, because half of it - the roles without the
 * exceptions - would name people who are in fact denied.
 */
export function HoldersTab() {
  const overview = useRolesOverview()
  const rights = useGetSettingsPermissionsEmployees()
  const [right, setRight] = useState('')

  const catalogue = useMemo(() => overview.data?.allPermissions ?? [], [overview.data?.allPermissions])
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const groups = useMemo(() => groupCatalogue(catalogue), [catalogue])

  if (overview.isError || rights.isError) {
    return (
      <TabReadFailed
        what="holders"
        retry={() => {
          if (overview.isError) void overview.refetch()
          if (rights.isError) void rights.refetch()
        }}
      >
        <T>Bez rolí a výnimiek by zoznam menoval aj ľudí, ktorým je právo zakázané. Preto sa neukazuje nič.</T>
      </TabReadFailed>
    )
  }

  if (overview.isLoading || rights.isLoading || overview.data === undefined || rights.data === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  const data = overview.data
  const byPerson = new Map<number, PersonRights>(
    rights.data.employees.map((row): [number, PersonRights] => [
      row.id,
      { overrides: row.overrides, allowed: allowedOf(row) },
    ]),
  )
  const answer =
    right === ''
      ? []
      : holdersOf(right, { people: data.people, roles: data.roles, catalogue, rights: byPerson })
  const checked = data.serverChecked.includes(right)

  return (
    <div className="space-y-4" data-holders-tab>
      <label className="block space-y-1 max-w-xl">
        <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {tt('Právo')}
        </span>
        <select
          value={right}
          onChange={(event) => setRight(event.target.value)}
          className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
          data-holders-right
        >
          <option value="">{tt('Vyberte právo')}</option>
          {groups.map((group) => (
            <optgroup key={group.key} label={group.heading}>
              {group.entries.map((entry) => (
                <option key={entry.permission} value={entry.permission}>
                  {entry.parent === null
                    ? permissionLabel(entry.permission, null)
                    : `${permissionLabel(entry.parent, null)} - ${permissionLabel(entry.permission, entry.parent)}`}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {right === '' ? (
        <p className="text-xs text-muted-foreground py-6 text-center">
          <T>Vyberte právo a uvidíte, kto ho má a prečo.</T>
        </p>
      ) : (
        <div className="space-y-3" data-holders-answer={right}>
          <div className="rounded-2xl border border-border bg-card p-4 space-y-1">
            <p className="text-sm font-bold">{permissionLabel(right, displayParentOf(right, known))}</p>
            {permissionDescription(right) !== null && (
              <p className="text-xs text-muted-foreground">{permissionDescription(right)}</p>
            )}
            <p className="text-xs" data-holders-count={holderCount(answer)}>
              {tt('Má ho')}: <strong>{formatCount(holderCount(answer))}</strong>
            </p>
            <p
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
              data-holders-server-checked={checked}
            >
              <Server className="w-3.5 h-3.5" />
              {checked
                ? tt('Toto právo kontroluje server - kto ho nemá, dostane odmietnutie.')
                : tt('Toto právo server nekontroluje - mení menu a obrazovku; čo obrazovka načíta, rozhoduje základ roly.')}
            </p>
          </div>

          {answer.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-holders-nobody>
              {tt('Toto právo nemá nikto.')}
            </p>
          ) : (
            answer.map((group) => (
              <div
                key={`${group.kind}:${group.roleKey ?? ''}`}
                className="rounded-2xl border border-border bg-card p-3 space-y-2"
                data-holders-group={group.roleKey === undefined ? group.kind : `role:${group.roleKey}`}
              >
                <h3 className="text-xs font-bold">
                  {groupHeading(group, (key) => roleNameOf(data.roles, key))} ({formatCount(group.people.length)})
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {group.people.map((person) => (
                    <span
                      key={person.id}
                      className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px]"
                      data-holders-person={person.id}
                    >
                      {person.name}
                      {group.kind !== 'role' && <RoleChip roleKey={effectiveRoleKey(person)} roles={data.roles} />}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function groupHeading(group: HolderGroup, nameOf: (key: string) => string): string {
  switch (group.kind) {
    case 'main-admin':
      return tt('Hlavní administrátori')
    case 'administrator-pass':
      return tt('Vstavaní administrátori - prejdú serverom, ak nemajú zákaz')
    case 'role':
      return `${tt('Rola')} ${nameOf(group.roleKey ?? '')}`
    case 'exception':
      return tt('Výnimkou udelené')
    case 'other':
      return tt('Server dovolí z iného dôvodu')
    case 'denied':
      return tt('Nemá - výnimkou zakázané')
  }
}
